<template>
  <v-expansion-panel>
    <v-expansion-panel-header style="height: 32px">
      <div class="d-flex align-center" style="gap: 0.5rem">
        <span>
          {{ $i18n.t('table.rziDocuments.authorization') }}
        </span>
        <span>
          <base-rounded-button
            icon="mdi-plus"
            size="x-small"
            color="success"
            @click.stop="openAuthorizationDialog()"
            :tooltip="$i18n.t('table.rziDocuments.addAuthorization')"
          />
        </span>
      </div>
      <add-edit-feature-dialog
        v-if="isAuthDialogLoaded"
        :is-visible.sync="isAuthDialogVisible"
        v-bind="authDialogData"
        @done="authDialogDone"
      />
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-row v-if="isFetched" no-gutters>
        <v-col v-if="!authorizationEntries.length" class="text--disabled text-subtitle-1 my-6">
          {{ $i18n.t('default.noData') }}
        </v-col>
        <v-col v-else>
          <template v-for="(item, index) in authorizationEntries || []">
            <v-list-item :key="index">
              <v-list-item-content>
                <v-row no-gutters class="pa-3 fill-height d-flex align-center">
                  <v-col class="d-flex flex-column">
                    <div style="height: 100%; gap: 1.5rem" class="d-flex flex-column align-start">
                      <div style="text-align: left; gap: 0.2rem" class="d-flex flex-column">
                        <div class="font-weight-medium">
                          {{ item.title }}
                        </div>
                        <div class="text-caption text--secondary font-italic">
                          {{ item.subtitle }}
                        </div>
                      </div>
                      <div class="text-body-2">
                        {{ item.bottom }}
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="auto" class="d-flex" style="gap: 0.5rem">
                    <span v-for="action in actions" :key="action.name">
                      <v-tooltip bottom color="font">
                        <template #activator="{ on }">
                          <v-icon v-on="on" @click="action.trigger(item.feature)">{{ action.icon }}</v-icon>
                        </template>
                        {{ action.tooltip }}
                      </v-tooltip>
                    </span>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
            <v-divider v-if="index !== authorizationEntries.length - 1" :key="`divider-${index}`"></v-divider>
          </template>
        </v-col>
        <main-dialog
          :v-if="isDeleteDialogLoaded"
          :is-visible.sync="isDeleteDialogVisible"
          :loading="isDeleteDialogLoading"
          :title="$i18n.t('table.rziDocuments.deleteAuthorization')"
          :body="$i18n.t('table.rziDocuments.deleteAuthorizationBody')"
          @agree="handleDelete"
          agree-text="dialog.agreeDelete"
        />
      </v-row>
      <v-row v-else no-gutters>
        <v-col class="my-6">
          <main-loader />
        </v-col>
      </v-row>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import MainLoader from '@/components/MainLoader';
import MainDialog from '@/components/MainDialog';

import { call } from 'vuex-pathify';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'RziAuthorizationList',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    AddEditFeatureDialog,
    MainLoader,
    MainDialog,
  },
  props: {
    currentMetadata: {
      type: Object,
    },
    documentData: {
      type: Object,
    },
    isOpened: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    isAuthDialogLoaded: false,
    isAuthDialogVisible: false,
    authDialogData: null,
    authorizationEntries: [],
    isFetched: false,
  }),
  computed: {
    authAttributes() {
      return this.currentMetadata.authorization.attributes;
    },
    actions() {
      return [
        {
          name: 'edit',
          icon: 'mdi-pencil',
          tooltip: this.$i18n.t('table.rziDocuments.editAuthorization'),
          trigger: this.openAuthorizationDialog,
        },
        {
          name: 'delete',
          icon: 'mdi-delete',
          tooltip: this.$i18n.t('table.rziDocuments.deleteAuthorization'),
          trigger: this.openDeleteDialog,
        },
      ];
    },
  },
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    openDeleteDialog(item) {
      this.itemToDelete = item.id;
      this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: this.currentMetadata.authorization.datasource,
          id: this.itemToDelete,
          params: {
            cascade: true,
          },
        });
        await this.init();
        this.itemToDelete = null;
        this.isDeleteDialogVisible = false;
      } catch (error) {
        console.error(error);
      } finally {
        this.isDeleteDialogLoading = false;
      }
    },
    openAuthorizationDialog(item) {
      this.authDialogData = {
        featureData: {
          ...(item && { id: item.id }),
          datasource: this.currentMetadata.authorization.datasource,
        },
        ...(item && { featureMetadata: item }),
        hiddenAttributes: ['_systems', this.authAttributes.document],
        staticProperties: {
          [this.authAttributes.document]: this.documentData.id,
        },
      };
      this.isAuthDialogLoaded = this.isAuthDialogVisible = true;
    },
    async authDialogDone(closeDialog) {
      await this.init();
      closeDialog();
    },
    getFormattedDate(date) {
      return date ? this.$dayjs(date).format('L') : '';
    },
    async init() {
      if (this.tabWatcher) this.tabWatcher();
      try {
        const r = await this.getDataSourceFeatures({
          dataSource: this.currentMetadata.authorization.datasource,
          filters: {
            $EQUAL: {
              [`${this.currentMetadata.authorization.datasource}.${this.authAttributes.document}`]: {
                value: this.documentData.id,
              },
            },
          },
        });
        this.authorizationEntries = r.features.map(f => ({
          feature: f,
          title: this.getAttributeTextValueByName(
            f.properties[this.authAttributes.institution],
            this.authAttributes.institution,
            this.currentMetadata.authorization.datasource
          ),
          subtitle: this.$i18n.t('table.rziDocuments.authorizationSubtitle', {
            date: this.getFormattedDate(f.properties[this.authAttributes.acceptanceDate]),
          }),
          bottom: this.$i18n.t('table.rziDocuments.authorizationBottom', {
            fromDate: this.getFormattedDate(f.properties[this.authAttributes.fromDate]),
            toDate: f.properties[this.authAttributes.dueDateBoolean]
              ? this.$i18n.t('table.rziDocuments.noDueDate')
              : this.getFormattedDate(f.properties[this.authAttributes.toDate]),
          }),
        }));
      } catch (error) {
        console.error(error);
      } finally {
        this.isFetched = true;
      }
    },
  },
  mounted() {
    this.tabWatcher = this.$watch(
      vm => vm.isOpened,
      nV => {
        if (nV) this.init();
      },
      {
        immediate: true,
      }
    );
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-list),
:deep(.v-list-item__content),
:deep(.v-list-item) {
  padding: 0 !important;
}
</style>
